import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ProductCalendar } from '../../../clients/quipli-service/availability/availability';
import { Location } from '../../../clients/quipli-service/location/location';
import { DateTime, Duration, Interval } from 'luxon';
import './inventory-gantt.scss';
import { ProductList } from './product-list';
import { ProductTimeline } from './product-timeline';
import classNames from 'classnames';
import _, { capitalize, Dictionary } from 'lodash';
import { Product } from '../../../clients/quipli-service/product/product';
import usePrevious from '../../../hooks/use-previous';
import { Spinner } from '../../../components/spinner/spinner';
import { useOnClickOutside } from '../../../util/use-on-click-outside';
import Container from '../../../components/container/container';
import { Button } from '../../../components/form/button';
import { Category } from '../../../clients/quipli-service/category/category';
import { Toggle } from '../../../components/form/toggle';
import { useCategoryApiV1 } from '../../../clients/quipli-service/category/category-api-v1';
import {
  isTimeDisabledInAllLocations,
  LocationInterval,
  useLocationInterval,
} from '../../../hooks/use-location-interval';

export type RangeUnit = 'month' | 'week' | 'day';

export type UnitRowHeight = { unit: number; height: number };
export type ProductRowHeight = { product: number; unitHeights: UnitRowHeight[] };
export const calcUnitRowHeight = (height?: number) => `${(height || 1) * 3}rem`;
export const InventoryGantt = ({
  locationIds,
  productIds,
  expandAll = false,
  startDateTime,
  duration,
  isDashboard = false,
}: {
  locationIds?: number[];
  productIds?: number[];
  expandAll?: boolean;
  startDateTime?: DateTime;
  duration?: Duration;
  isDashboard?: boolean;
}) => {
  const api = useAPI();
  const { listCategories } = useCategoryApiV1();
  const [expandedProducts, setExpandedProducts] = useState<number[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<number[]>([]);
  const [productCalendars, setProductCalendars] =
    useState<{ calendar: ProductCalendar[] | undefined; range: Interval }[]>();
  const [rangeUnit, setRangeUnit] = useState<RangeUnit>('month');
  const [anchorDateTime, setAnchorDateTime] = useState<DateTime>();
  const [locations, setLocations] = useState<Location[]>([]);
  const [productList, setProductList] = useState<Product[]>([]);
  const [filteredLocation, setFilteredLocation] = useState<Location>();
  const [productRowHeights, setProductRowHeights] = useState<ProductRowHeight[]>([]);
  const [ranges, setRanges] = useState<Interval[]>();
  const [search, setSearch] = useState<string>('');
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const previousRanges = usePrevious(ranges);
  const [scrollNodes, setScrollNodes] = useState<HTMLOListElement[]>([]);
  const [showCategories, setShowCategories] = useState<boolean>(isDashboard);
  const [categories, setCategories] = useState<Category[]>();
  const [serialNumberEnabled, setSerialNumberEnabled] = useState<boolean>(false);

  const selectedDayIntervals = useLocationInterval(
    filteredLocation ? [filteredLocation] : locations,
    anchorDateTime,
    true,
  );

  function handleScroll(e: HTMLOListElement) {
    scrollNodes.forEach((n) => {
      n.onscroll = null;
      n.scrollTop = e.scrollTop;
      bindScrollEvent(n);
    });
  }

  function bindScrollEvent(e: HTMLOListElement) {
    e.onscroll = () => handleScroll(e);
  }

  useEffect(() => {
    const getCategories = async () => {
      const categories = await listCategories();
      setCategories(categories.filter((c) => c.isActive));
      setExpandedCategories(categories.map((c) => c.id));
    };

    getCategories();
  }, []);

  useEffect(() => {
    scrollNodes.forEach((n) => {
      bindScrollEvent(n);
    });
  }, [scrollNodes]);

  useEffect(() => {
    if (anchorDateTime && anchorDateTime.isValid) {
      const correctionFactor = rangeUnit === 'week' ? { days: 1 } : 0;
      let newRanges;
      if (!duration || duration.as('milliseconds') <= 0) {
        newRanges = [
          Interval.fromDateTimes(
            anchorDateTime
              .minus({ [rangeUnit]: 1 })
              .startOf(rangeUnit)
              .minus(correctionFactor),
            anchorDateTime
              .minus({ [rangeUnit]: 1 })
              .endOf(rangeUnit)
              .minus(correctionFactor),
          ),
          Interval.fromDateTimes(
            anchorDateTime.startOf(rangeUnit).minus(correctionFactor),
            anchorDateTime.endOf(rangeUnit).minus(correctionFactor),
          ),
          Interval.fromDateTimes(
            anchorDateTime
              .plus({ [rangeUnit]: 1 })
              .startOf(rangeUnit)
              .minus(correctionFactor),
            anchorDateTime
              .plus({ [rangeUnit]: 1 })
              .endOf(rangeUnit)
              .minus(correctionFactor),
          ),
        ];
      } else {
        newRanges = [
          Interval.fromDateTimes(anchorDateTime.minus(duration), anchorDateTime),
          Interval.fromDateTimes(anchorDateTime, anchorDateTime.plus(duration)),
          Interval.fromDateTimes(anchorDateTime.plus(duration), anchorDateTime.plus(duration).plus(duration)),
        ];
      }
      if (
        !ranges ||
        ranges.length !== 3 ||
        ranges[1].start.diff(newRanges[1].start).milliseconds !== 0 ||
        ranges[1].end.diff(newRanges[1].end).milliseconds !== 0
      ) {
        setRanges(newRanges);
        setProductCalendars([
          {
            calendar: undefined,
            range: newRanges[0],
          },
          {
            calendar: undefined,
            range: newRanges[1],
          },
          {
            calendar: undefined,
            range: newRanges[2],
          },
        ]);
      }
    }
  }, [anchorDateTime, ranges, rangeUnit, duration]);

  useEffect(() => {
    const fetchUnitTrackingConfig = async () => {
      const config = await api.getConfiguration('CONF_UNIT_TRACKING_VIA');
      setSerialNumberEnabled(config?.value === '1');
    };
    fetchUnitTrackingConfig();
  }, []);

  async function refreshScheduleDatatData(
    ranges: Interval[],
    productIds: number[] | undefined,
    locationIds: number[] | undefined,
    productCalendars: { calendar: ProductCalendar[] | undefined; range: Interval }[],
    expandAll: boolean,
  ) {
    function fetchProductCalendar(i: number): Promise<ProductCalendar[] | undefined> {
      if (ranges.length > i) {
        return productCalendars[i].calendar === undefined || !productCalendars[i].range.equals(ranges[i])
          ? api.getProductCalendar(productIds, locationIds, ranges[i].start, ranges[i].end)
          : Promise.resolve(productCalendars[i].calendar);
      } else {
        return Promise.resolve(undefined);
      }
    }

    const currentData = await fetchProductCalendar(1);
    setProductCalendars(
      (pcs) =>
        pcs && [
          pcs[0],
          ranges[1].equals(pcs[1].range) ? { range: pcs[1].range, calendar: currentData } : pcs[1],
          pcs[2],
        ],
    );
    if (currentData) {
      const products = currentData.map((pc) => pc.product);
      setProductList(products);
    }
    if (currentData && expandAll) {
      setExpandedProducts(currentData.map((ps) => ps.product.id));
    }
    setIsInitialLoad(false);
    const otherData = await Promise.all([fetchProductCalendar(0), fetchProductCalendar(2)]);
    setProductCalendars(
      (pss) =>
        pss && [
          ranges[0].equals(pss[0].range) ? { range: pss[0].range, calendar: otherData[0] } : pss[0],
          pss[1],
          ranges[2].equals(pss[2].range) ? { range: pss[2].range, calendar: otherData[1] } : pss[2],
        ],
    );
  }

  const debounceRefreshScheduleDatatData = useRef(_.debounce(refreshScheduleDatatData, 100)).current;

  useEffect(() => {
    if (
      ranges?.length &&
      productCalendars &&
      (!previousRanges || ranges.some((r, i) => !r.equals(previousRanges[i])))
    ) {
      debounceRefreshScheduleDatatData(
        ranges,
        productIds,
        filteredLocation ? [filteredLocation.id] : locationIds,
        productCalendars,
        expandAll,
      );
    }
  }, [filteredLocation, previousRanges, ranges, productCalendars, productIds, locationIds, expandAll]);

  useEffect(() => {
    const doGetLocation = async () => {
      const locations = await api.listLocations();
      setLocations(locations);
    };

    doGetLocation();
  }, []);

  useEffect(() => {
    const filteredLocations = locations.filter((l) => !locationIds || locationIds.indexOf(l.id) >= 0);
    if (filteredLocations.length > 0) {
      setAnchorDateTime((adt) => {
        return (adt || startDateTime || DateTime.now()).setZone(locations[0].timezone);
      });
    }
  }, [locations, locationIds]);

  const nextRange = useCallback(() => {
    if (ranges?.length && productCalendars?.length && anchorDateTime) {
      let next;
      if (duration) {
        next = Interval.fromDateTimes(ranges[2].start.plus(duration), ranges[2].end.plus(duration));
        setAnchorDateTime(anchorDateTime.plus(duration));
      } else {
        const start = ranges[2].start.plus({ [rangeUnit]: 1 });
        next = Interval.fromDateTimes(start.startOf(rangeUnit), start.endOf(rangeUnit));
        setAnchorDateTime(anchorDateTime.plus({ [rangeUnit]: 1 }));
      }
      setRanges([...ranges.slice(1), next]);
      setProductCalendars([...productCalendars.slice(1), { range: next, calendar: undefined }]);
    }
  }, [ranges, productCalendars, rangeUnit, anchorDateTime]);

  const previousRange = useCallback(() => {
    if (ranges?.length && productCalendars?.length && anchorDateTime) {
      let previous;
      if (duration) {
        previous = Interval.fromDateTimes(ranges[0].start.minus(duration), ranges[0].end.minus(duration));
        setAnchorDateTime(anchorDateTime.minus(duration));
      } else {
        const start = ranges[0].start.minus({ [rangeUnit]: 1 });
        previous = Interval.fromDateTimes(start.startOf(rangeUnit), start.endOf(rangeUnit));
        setAnchorDateTime(anchorDateTime.minus({ [rangeUnit]: 1 }));
      }
      setRanges([previous, ...ranges.slice(0, 2)]);
      setProductCalendars([{ range: previous, calendar: undefined }, ...productCalendars.slice(0, 2)]);
    }
  }, [ranges, productCalendars, rangeUnit, anchorDateTime]);

  const productFilter = useCallback(
    (product: Product) => {
      let matchesSearch = true;
      if (search) {
        const terms = search
          .toLowerCase()
          .split('w')
          .map((t) => t.replace(/[^0-9a-z]/gi, ''));
        matchesSearch = terms.every((t) => {
          return (
            product.name
              .toLowerCase()
              .replace(/[^0-9a-z]/gi, '')
              .indexOf(t) >= 0 ||
            product.inventoryUnits.find(
              (unit) => unit.serialNumber.toLowerCase().includes(t) || unit.unitNumber.toLowerCase().includes(t),
            )
          );
        });
      }

      let matchesLocationFilter = true;
      if (filteredLocation) {
        matchesLocationFilter = product.locationIds.indexOf(filteredLocation.id) >= 0;
      }
      return matchesSearch && matchesLocationFilter && product.isActive;
    },
    [search],
  );

  function selectFilteredLocation(location: Location | undefined) {
    setFilteredLocation(location);
    if (ranges) {
      refreshScheduleDatatData(
        ranges,
        productIds,
        location ? [location.id] : locationIds,
        [
          { range: ranges[0], calendar: undefined },
          { range: ranges[1], calendar: undefined },
          { range: ranges[2], calendar: undefined },
        ],
        expandAll,
      );
    }
  }

  const filteredProducts = productList.filter(productFilter);
  const filteredCategories = categories?.filter((c) => filteredProducts.find((p) => p.categoryIds.indexOf(c.id) >= 0));
  const haveActiveCategories = (filteredCategories?.length ?? 0) > 0;

  return (
    <div className="inventory-gantt-wrapper">
      {ranges && productCalendars && !isInitialLoad ? (
        <div className="inventory-gantt">
          <div className="inventory-gantt-header">
            {isDashboard && (
              <>
                <h1>Inventory Calendar</h1>
                <div className="search-input">
                  <div className="search">
                    <i className="fa fa-search" />
                    <div>
                      <input placeholder="Search" onChange={(v) => setSearch(v.target.value)} value={search}></input>
                    </div>
                  </div>
                  <LocationFilter
                    filteredLocation={filteredLocation}
                    setFilteredLocation={selectFilteredLocation}
                    locations={locations}
                  />
                </div>
                {haveActiveCategories && (
                  <div className="show-categories-control">
                    <label>Show Categories:</label>
                    <Toggle onClick={() => setShowCategories(!showCategories)} checked={showCategories} />
                  </div>
                )}
              </>
            )}

            <div className="controls">
              {isDashboard ? (
                <Button
                  className="button button-secondary"
                  onClick={() => setAnchorDateTime(DateTime.now().setZone(anchorDateTime?.zone))}
                >
                  Today
                </Button>
              ) : (
                <div></div>
              )}
              <PaginationControl range={ranges[1]} nextRange={nextRange} previousRange={previousRange} />
              <div className="buttons">
                {isDashboard && <RangeUnitPicker rangeUnit={rangeUnit} setRangeUnit={setRangeUnit} />}
              </div>
            </div>
          </div>

          <Container className={classNames('inventory-gantt-body', isDashboard ? 'max-h-[80vh]' : '')}>
            <ProductList
              registerScrollNode={(n: HTMLOListElement) => setScrollNodes((nodes) => [n, ...nodes])}
              products={filteredProducts}
              locations={locations}
              categories={showCategories && haveActiveCategories ? filteredCategories : undefined}
              expandedCategories={expandedCategories}
              setExpandedCategories={setExpandedCategories}
              productCalendars={productCalendars}
              expandedProducts={expandedProducts}
              productRowHeights={productRowHeights}
              setExpandedProducts={setExpandedProducts}
              rangeUnit={rangeUnit}
              serialNumberEnabled={serialNumberEnabled}
              isDashboard={isDashboard}
            />
            <ProductTimeline
              registerScrollNode={(n: HTMLOListElement) => setScrollNodes((nodes) => [n, ...nodes])}
              productCalendars={
                productCalendars[1].calendar?.filter((pc) => filteredProducts.some((p) => p.id === pc.product.id)) || []
              }
              locations={locations}
              categories={showCategories && haveActiveCategories ? filteredCategories : undefined}
              expandedCategories={expandedCategories}
              isLoading={productCalendars[1] === undefined}
              range={filterRangeToShopHours(ranges[1], selectedDayIntervals.locationIntervals, filteredLocation)}
              expandedProducts={expandedProducts}
              productRowHeights={productRowHeights}
              setProductRowHeights={(product, unit, height) =>
                setProductRowHeights((old) => {
                  const row: ProductRowHeight = old.find((prh) => prh.product === product) || {
                    product,
                    unitHeights: [],
                  };
                  const updated = {
                    product,
                    unitHeights: [{ unit, height }, ...row.unitHeights.filter((uh) => uh.unit !== unit)],
                  };
                  return [updated, ...old.filter((prh) => prh.product !== product)];
                })
              }
              serialNumberEnabled={serialNumberEnabled}
              isDashboard={isDashboard}
            />
          </Container>
        </div>
      ) : (
        <Spinner />
      )}
    </div>
  );
};

const RangeUnitPicker = ({
  rangeUnit,
  setRangeUnit,
}: {
  rangeUnit: RangeUnit;
  setRangeUnit: (r: RangeUnit) => void;
}) => {
  const RangeButton = ({ buttonUnit }: { buttonUnit: RangeUnit }) => {
    return (
      <div
        className={classNames('button', { selected: buttonUnit === rangeUnit })}
        onClick={() => setRangeUnit(buttonUnit)}
      >
        {capitalize(buttonUnit)}
      </div>
    );
  };

  return (
    <div className="range-unit-picker">
      <RangeButton buttonUnit="day" />
      <RangeButton buttonUnit="week" />
      <RangeButton buttonUnit="month" />
    </div>
  );
};

const LocationFilter = ({
  filteredLocation,
  setFilteredLocation,
  locations,
}: {
  filteredLocation: Location | undefined;
  setFilteredLocation: (l: Location | undefined) => void;
  locations: Location[];
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const divRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(divRef, () => setIsOpen(false));

  function handleSelection(location: Location | undefined) {
    setFilteredLocation(location);
    //give time for on click to fire first
    setTimeout(() => setIsOpen(false), 0);
  }

  return (
    <>
      {locations.length > 1 && (
        <div ref={divRef} className="location-filter" onClick={() => setIsOpen(true)}>
          <i className="fa fa-home"></i>
          <span>{filteredLocation ? filteredLocation.name : 'All Locations'}</span>
          {isOpen && (
            <ul className="location-options">
              <li onClick={() => handleSelection(undefined)} key={-1}>
                All Locations
              </li>
              {locations.map((l) => (
                <li onClick={() => handleSelection(l)} key={l.id}>
                  {l.name}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );
};

const PaginationControl = ({
  range,
  nextRange,
  previousRange,
}: {
  range: Interval;
  nextRange: () => void;
  previousRange: () => void;
}) => {
  let rangeUnit: 'month' | 'day' | 'week' | undefined, label: string;
  if (
    Math.abs(range.start.diff(range.start.startOf('month')).as('minute')) <= 1 &&
    Math.abs(range.end.diff(range.start.endOf('month')).as('minute')) <= 1
  ) {
    rangeUnit = 'month';
  } else if (
    Math.abs(range.start.diff(range.start.startOf('week')).as('minute')) <= 1 &&
    Math.abs(range.end.diff(range.start.endOf('week')).as('minute')) <= 1
  ) {
    rangeUnit = 'week';
  } else if (
    Math.abs(range.start.diff(range.start.startOf('day')).as('minute')) <= 1 &&
    Math.abs(range.end.diff(range.start.endOf('day')).as('minute')) <= 1
  ) {
    rangeUnit = 'day';
  }
  switch (rangeUnit) {
    case 'month':
    case 'week':
      label = range.start.toFormat('MMMM y');
      break;
    case 'day':
      label = range.start.toFormat('DDD');
      break;
    default:
      label = `${range.start.toFormat('DDD')} - ${range.end.toFormat('DDD')}`;
  }

  return (
    <div className="pagination-control">
      <i className="fa fa-angle-left" onClick={previousRange} />
      <i className="fa fa-angle-right" onClick={nextRange} />
      <span>{label}</span>
    </div>
  );
};

const filterRangeToShopHours = (
  interval: Interval,
  shopHours: Dictionary<LocationInterval>,
  filteredLocation: Location | undefined,
) => {
  if (interval.length('days') <= 1) {
    const locationId = filteredLocation ? filteredLocation.id : undefined;
    let lookupDict = shopHours;
    if (locationId) {
      lookupDict = { [locationId]: shopHours[locationId] };
    }
    const intervalIntoHours = interval.splitBy({ hours: 1 });
    const filteredIntervals = intervalIntoHours.filter((i) => {
      return !isTimeDisabledInAllLocations(i.start, lookupDict);
    });

    return filteredIntervals.length > 0
      ? Interval.fromDateTimes(filteredIntervals[0].start, filteredIntervals[filteredIntervals.length - 1].end)
      : interval;
  } else {
    return interval;
  }
};
